import { LineDirectionX, LineDirectionY } from 'types'
import { xAlongLine, yAlongLine } from 'utils/math'

const boundsStartU = 0
const boundsStartV = 0
const boundsEndU = 1
const boundsEndV = 1

/**
 * If the polygon defined by uvs wraps, then create a polygon for the left (or bottom in y direction)
 * side and right (or top in y direction).
 * @param uvs
 * @param lineXDirections
 * @param lineYDirections
 * @param wrapsX
 * @param wrapsY
 * @returns
 */
export const createPolygonsForWrapping = (
  uvs: number[],
  lineXDirections: ReadonlyArray<LineDirectionX>,
  lineYDirections: ReadonlyArray<LineDirectionY>,
  wrapsX: boolean,
  wrapsY: boolean
): number[][] => {
  if (!wrapsX && !wrapsY) return [uvs]

  let polygonDoesntWrap = true
  let startsOnBottomOrLeft = true
  for (let i = 0; i < uvs.length - 2; i = i + 2) {
    const sx = uvs[i]
    const sy = uvs[i + 1]
    const ex = uvs[i + 2]
    const ey = uvs[i + 3]
    const xDirection = lineXDirections[Math.floor(i / 2)]
    const yDirection = lineYDirections[Math.floor(i / 2)]
    const lineIntersects = wrapsX
      ? crossesXBoundary(sx, ex, xDirection)
      : crossesYBoundary(sy, ey, yDirection)
    if (lineIntersects) {
      polygonDoesntWrap = false
      startsOnBottomOrLeft = wrapsX
        ? xDirection === LineDirectionX.Right
        : yDirection === LineDirectionY.Up
      break
    }
  }
  if (polygonDoesntWrap) return [uvs]

  const polygon: number[] = []
  const partnerPolygon: number[] = []

  let currentlyOnBottomOrLeft = startsOnBottomOrLeft

  for (let i = 0; i < uvs.length; i = i + 2) {
    const u = uvs[i]
    const v = uvs[i + 1]
    const xDirection = lineXDirections[Math.floor(i / 2)]
    const yDirection = lineYDirections[Math.floor(i / 2)]

    const partnerU = wrapsX
      ? currentlyOnBottomOrLeft
        ? u - boundsEndU
        : u + boundsEndU
      : u
    const partnerV = wrapsY
      ? currentlyOnBottomOrLeft
        ? v - boundsEndV
        : v + boundsEndV
      : v
    if (currentlyOnBottomOrLeft === startsOnBottomOrLeft) {
      polygon.push(u, v)
      partnerPolygon.push(partnerU, partnerV)
    } else {
      polygon.push(partnerU, partnerV)
      partnerPolygon.push(u, v)
    }
    const lineIntersects = wrapsX
      ? crossesXBoundary(u, uvs.at(i + 2), xDirection)
      : crossesYBoundary(v, uvs.at(i + 3), yDirection)
    if (lineIntersects) currentlyOnBottomOrLeft = !currentlyOnBottomOrLeft
  }
  return [polygon, partnerPolygon]
}

/**
 * Splits a line segment along the x or y wrapping points, creates boundary segments if applicable,
 * and adds all resulting segments to the list uvs.
 * @param su start u
 * @param sv start v
 * @param eu end u
 * @param ev end v
 * @param uvs list to add to
 * @param xDir x direction of s -> e
 * @param yDir y direction of s -> e
 * @param wrapsX boolean for component wraps x
 * @param wrapsY boolean for component wraps y
 * @param createBoundarySegments whether extra segments should be created for segments that are on
 * boundaries (for unrolled wrapped components)
 */
export const addUVSegmentAndWrapProperly = (
  su: number,
  sv: number,
  eu: number,
  ev: number,
  uvs: number[],
  xDir: LineDirectionX,
  yDir: LineDirectionY,
  wrapsX: boolean,
  wrapsY: boolean,
  createBoundarySegments: boolean
): void => {
  if (!wrapsX && !wrapsY) {
    uvs.push(su, sv, eu, ev)
    return
  }
  uvs.push(su, sv)
  const intersection = wrapsX
    ? findXBoundaryIntersectionIfPresent(su, sv, eu, ev, xDir)
    : findYBoundaryIntersectionIfPresent(su, sv, eu, ev, yDir)
  if (intersection === undefined) {
    uvs.push(eu, ev)
    if (createBoundarySegments) {
      if (wrapsX && segmentAlongXBoundary(su, eu)) {
        if (su === boundsStartU) {
          uvs.push(boundsEndU, sv, boundsEndU, ev)
        } else if (su === boundsEndU) {
          uvs.push(boundsStartU, sv, boundsStartU, ev)
        }
      } else if (wrapsY && segmentAlongYBoundary(sv, ev)) {
        if (sv === boundsStartV) {
          uvs.push(su, boundsEndV, eu, boundsEndV)
        } else if (sv === boundsEndV) {
          uvs.push(su, boundsStartV, eu, boundsStartV)
        }
      }
    }
  } else {
    const endIntersect: [number, number] = wrapsX
      ? xDir === LineDirectionX.Right
        ? [boundsEndU, intersection]
        : [boundsStartU, intersection]
      : yDir === LineDirectionY.Up
        ? [intersection, boundsEndV]
        : [intersection, boundsStartV]

    const nextEndIntersect: [number, number] = wrapsX
      ? xDir === LineDirectionX.Left
        ? [boundsEndU, intersection]
        : [boundsStartU, intersection]
      : yDir === LineDirectionY.Down
        ? [intersection, boundsEndV]
        : [intersection, boundsStartV]
    uvs.push(...endIntersect, ...nextEndIntersect, eu, ev)
  }
}

export const crossesXBoundary = (
  startU: number,
  endU: number | undefined,
  xDirection: LineDirectionX
): boolean => {
  if (endU === undefined || segmentAlongXBoundary(startU, endU)) return false
  return (
    xDirection !== LineDirectionX.None &&
    (startU === endU || startU < endU !== (xDirection === LineDirectionX.Right))
  )
}

export const crossesYBoundary = (
  startV: number,
  endV: number | undefined,
  yDirection: LineDirectionY
): boolean => {
  if (endV === undefined || segmentAlongYBoundary(startV, endV)) return false
  return (
    yDirection !== LineDirectionY.None &&
    (startV === endV || startV < endV !== (yDirection === LineDirectionY.Up))
  )
}

const segmentAlongXBoundary = (startU: number, endU: number) =>
  startU === endU && (startU === boundsEndU || startU === boundsStartU)
const segmentAlongYBoundary = (startV: number, endV: number) =>
  startV === endV && (startV === boundsEndV || startV === boundsStartV)

/**
 * Finds intersection with the X wrapping boundary.
 * If there's no intersection or no endpoint, returns undefined.
 * @param startU
 * @param startV
 * @param endU
 * @param endV
 * @param xDirection
 * @returns
 */
const findXBoundaryIntersectionIfPresent = (
  startU: number,
  startV: number,
  endU: number,
  endV: number,
  xDirection: LineDirectionX
): number | undefined => {
  if (
    endU === undefined ||
    endV === undefined ||
    segmentAlongXBoundary(startU, endU)
  )
    return undefined
  if (
    xDirection !== LineDirectionX.None &&
    startU < endU !== (xDirection === LineDirectionX.Right)
  ) {
    if (xDirection === LineDirectionX.Right) {
      const newEndU = boundsEndU + endU
      return yAlongLine([startU, startV], [newEndU, endV], boundsEndU)
    } else {
      const newEndU = endU - boundsEndU
      return yAlongLine([startU, startV], [newEndU, endV], boundsStartU)
    }
  }
  return undefined
}

/**
 * Finds intersection with the Y wrapping boundary.
 * If there's no intersection or no endpoint, returns undefined.
 * @param startU
 * @param startV
 * @param endU
 * @param endV
 * @param yDirection
 * @returns
 */
const findYBoundaryIntersectionIfPresent = (
  startU: number,
  startV: number,
  endU: number,
  endV: number,
  yDirection: LineDirectionY
): number | undefined => {
  if (
    endU === undefined ||
    endV === undefined ||
    segmentAlongYBoundary(startV, endV)
  )
    return undefined
  if (
    yDirection !== LineDirectionY.None &&
    startV < endV !== (yDirection === LineDirectionY.Up)
  ) {
    if (yDirection === LineDirectionY.Up) {
      const newEndV = boundsEndV + endV
      return xAlongLine([startU, startV], [endU, newEndV], boundsEndV)
    } else {
      const newEndV = endV - boundsEndV
      return xAlongLine([startU, startV], [endU, newEndV], boundsStartV)
    }
  }
  return undefined
}
